import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTableContext } from "../../context/TableContext";
import html2pdf from "html2pdf.js";
import fng_logo from "../../assets/images/fng_logo_black.png";
import parse from "html-react-parser";
import {
  finalizeLaborSummaryRow,
  materialNameBaseForEdit,
} from "../../utils/materialReference";
import {
  getOfficeFieldCopyCrewLaborRowFields,
  getOfficeFieldCopyLineCost,
  getOfficeFieldCopyRowCalculations,
  getCustomerCopyDisplayDescription,
  getCustomerCopyMaterialsTableDisplayDescription,
  getCustomerCopyMaterialsTableMergeKey,
  calcProjectFuelSurcharge,
  isFuelSurchargeEnabledOnProject,
  isLumpSumFieldCopySource,
  isLumpSumLaborFieldCopyItem,
  lookupJobTypeCatalogRate,
  lookupLaborMapValue,
  mergeCustomerCopyInvoiceSummaryRows,
  mergeLaborMapMissingKeys,
  normalizeJobTypeKey,
  resolveCustomerCopyCrewLaborTaxable,
  resolveFieldCopyCrewUnitRate,
  resolveFieldCopyDisplayJobType,
  resolveOfficeFieldCopyGroupJobType,
} from "../../utils/fieldCopyLaborDisplay";

/** PDF SIZE header/data shift — column width unchanged. */
const PDF_SIZE_COLUMN_SHIFT = "30px";
/** PDF QUANTITY header padding — +4px right so long SIZE wrap overlap na ho. */
const PDF_QTY_COLUMN_SHIFT = "44px";

function formatPdfSizeDisplay(value) {
  // PDF SIZE cell — full text, single line with dynamic padding.
  const text =
    value === null || value === undefined || value === ""
      ? ""
      : String(value).trim();
  return text;
}

/** SIZE data — shorter text keeps default shift; longer text uses less padding to fit one line. */
function getPdfSizeDataPaddingLeft(value) {
  const text = formatPdfSizeDisplay(value);
  if (!text) return PDF_SIZE_COLUMN_SHIFT;
  const len = text.length;
  if (len <= 4) return "30px";
  if (len <= 6) return "24px";
  if (len <= 8) return "18px";
  if (len <= 10) return "12px";
  if (len <= 12) return "8px";
  if (len <= 14) return "4px";
  return "2px";
}

function getPdfSizeCellStyle(value) {
  const text = formatPdfSizeDisplay(value);
  const len = text.length;
  return {
    textAlign: "center",
    paddingLeft: getPdfSizeDataPaddingLeft(value),
    paddingRight: len > 8 ? "0px" : "2px",
    whiteSpace: "nowrap",
    verticalAlign: "top",
  };
}

/** PDF QTY data padding — shorter values nudge right; longer values need less. */
function getPdfQtyDataPaddingLeft(value) {
  const text =
    value === null || value === undefined || value === ""
      ? ""
      : String(value).trim();
  if (!text) return PDF_QTY_COLUMN_SHIFT;
  const len = text.length;
  if (len <= 1) return "62px";
  if (len <= 2) return "58px";
  if (len <= 3) return "54px";
  if (len <= 4) return "52px";
  if (len <= 5) return "50px";
  return "48px";
}

/** PDF QTY — plain text; truncate only when long so PRICE overlap na ho. */
function formatPdfQtyDisplay(value) {
  const text =
    value === null || value === undefined || value === ""
      ? ""
      : String(value).trim();
  if (!text) return "";
  return text.length > 6 ? text.slice(0, 6) : text;
}

function getPdfQtyCellStyle(value) {
  return {
    textAlign: "center",
    paddingLeft: getPdfQtyDataPaddingLeft(value),
    paddingRight: "4px",
    whiteSpace: "nowrap",
    verticalAlign: "top",
    fontVariantNumeric: "tabular-nums",
  };
}

/** Customer Copy Materials table — crew rows use sell total, not internal office cost. */
function customerCopyCrewTableCalc(crewFields) {
  const displayTotal = Number(crewFields?.displayTotal) || 0;
  const qtyText = crewFields?.qtyText ?? "";
  const qty = parseFloat(qtyText) || 0;

  if (!(displayTotal > 0)) {
    return {
      lineCost: crewFields?.lineCost,
      displayPrice: crewFields?.displayPrice,
      markupVal: crewFields?.markupVal,
      displayTotal,
      qtyText,
    };
  }

  const lineCost = Math.round((displayTotal / 2) * 100) / 100;
  const displayPrice =
    qty > 0
      ? Math.round((displayTotal / qty) * 100) / 100
      : crewFields?.displayPrice;

  return {
    lineCost,
    displayPrice,
    markupVal: 100,
    displayTotal,
    qtyText,
  };
}

/** Penny-rounded sell totals show ~99.99% when markup is 100% (sell ≈ 2× cost). */
function snapCustomerCopyMarkupPercent(markupVal, lineCost, displayTotal) {
  const stored = Number(markupVal);
  if (Number.isFinite(stored) && Math.abs(stored - 100) < 0.06) return 100;

  const lc = Number(lineCost) || 0;
  const dt = Number(displayTotal) || 0;
  if (lc > 0 && dt > 0) {
    const implied = Math.round(((dt / lc - 1) * 100) * 100) / 100;
    if (Math.abs(dt - lc * 2) <= 0.02 || (implied >= 99.95 && implied <= 100.05)) {
      return 100;
    }
    return implied;
  }
  const m = Number(markupVal);
  if (Number.isFinite(m) && m >= 99.95 && m <= 100.05) return 100;
  return markupVal;
}

function formatCustomerCopyMarkupCell(markupVal, lineCost, displayTotal) {
  const snapped = snapCustomerCopyMarkupPercent(markupVal, lineCost, displayTotal);
  if (snapped === null || snapped === undefined || snapped === "") return "";
  return (
    Number(snapped).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + "%"
  );
}

export default function CustomerFieldCopyView() {
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    billingType: "",
    jobAddress: "",
    jobType: "",
    crewCategory: "",
    description: "",
    crew: [],
    truckNo: "",
    trailerNo: "",
    projectStartDate: "",
    status: "",
    credits: 0,
    projectCompletedDate: "",
    officeCopyId: "",
    bidCopyId: "",
    billAddress: "",
    jobName: "",
    isProjectTaxable: false,
    isFuelSurchargeEnabled: true,
    fuelSurchargePercent: 1.5,
    customerType: "",
    nonTaxCredits: 0,
    nonTaxDescription: "",
    taxCredits: 0,
    taxDescription: "",
    billingName: ""
  });
  const [fieldCopies, setFieldCopies] = useState([]);
  const [jobType, setJobType] = useState("");
  const [loading, setLoading] = useState(false);
  const [laborTotal, setLaborTotal] = useState(0);
  const [fieldLaborData, SetFieldLaborData] = useState([]);
  const [jobTypesCatalog, setJobTypesCatalog] = useState([]);
  const [materialsTotal, setMaterialsTotal] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [taxableAmount, setTaxableAmount] = useState(0);
  const [materialData, setMaterialData] = useState([]);
  const [laborData, setLaborData] = useState([]);
  const [categorizedFieldCopies, setCategorizedFieldCopies] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [address, setAddress] = useState("");
  const [materialLaborData, setMaterialLaborData] = useState([]);

  const location = useLocation();
  const data = location.state?.data ?? null;

  const { id, entryDate, index } = useParams();

  const navigate = useNavigate();
  const { tableSize } = useTableContext();

  const buildOfficeLaborMaps = useCallback(
    (filterByEntryDate) => {
      const hoursMap = {};
      const rateMap = {};
      const catalogOpts = { jobTypesCatalog };

      const pushFromGroups = (groups) => {
        if (!Array.isArray(groups)) return;
        for (const g of groups) {
          const jt = resolveOfficeFieldCopyGroupJobType(g, catalogOpts);
          if (!jt) continue;
          const h = Number(g?.manHours || 0);
          if (h > 0) hoursMap[jt] = (hoursMap[jt] || 0) + h;
          const directRate = Number(g?.jobTypeCost || 0);
          const derivedRate =
            h > 0 ? Number(g?.totalCost || 0) / h : 0;
          const rate =
            directRate > 0 ? directRate : derivedRate > 0 ? derivedRate : 0;
          if (rate > 0 && (!(rateMap[jt] > 0) || rateMap[jt] <= 0)) {
            rateMap[jt] = rate;
          }
        }
      };

      const wantDate = String(entryDate || "").trim();
      const pushOfficeEntries = (entries, applyDateFilter) => {
        if (!Array.isArray(entries)) return;
        for (const entry of entries) {
          if (applyDateFilter && wantDate) {
            const entryKey = String(entry?.entryDate || "").trim();
            if (entryKey && entryKey !== wantDate) continue;
          }
          pushFromGroups(entry?.fieldCopies);
        }
      };

      pushOfficeEntries(formData?.officeFieldCopy, filterByEntryDate);
      pushOfficeEntries(
        formData?.draftCopy?.map((d) => ({
          entryDate: d?.entryDate,
          fieldCopies: d?.draftCopies,
        })),
        filterByEntryDate
      );

      for (const labor of fieldLaborData || []) {
        const jt = resolveFieldCopyDisplayJobType({
          jobType: labor?.jobType,
          formDataJobType: formData?.jobType,
        });
        if (!jt) continue;
        const h =
          Number(labor?.manHours) > 0
            ? Number(labor.manHours)
            : Number(labor?.quantity) > 0
              ? Number(labor.quantity)
              : 0;
        if (h > 0) hoursMap[jt] = (hoursMap[jt] || 0) + h;
      }

      for (const jt of Object.keys(hoursMap)) {
        if (!(rateMap[jt] > 0)) {
          const catalogRate = lookupJobTypeCatalogRate(jobTypesCatalog, jt);
          if (catalogRate > 0) rateMap[jt] = catalogRate;
        }
      }

      return { hoursMap, rateMap };
    },
    [
      entryDate,
      fieldLaborData,
      formData?.draftCopy,
      formData?.jobType,
      formData?.officeFieldCopy,
      jobTypesCatalog,
    ]
  );

  const { laborHourlyRateByJobType, laborManHoursByJobType } = useMemo(() => {
    const forDate = buildOfficeLaborMaps(true);
    const allDates = buildOfficeLaborMaps(false);
    return {
      laborHourlyRateByJobType: mergeLaborMapMissingKeys(
        forDate.rateMap,
        allDates.rateMap
      ),
      laborManHoursByJobType: mergeLaborMapMissingKeys(
        forDate.hoursMap,
        allDates.hoursMap
      ),
    };
  }, [buildOfficeLaborMaps]);

  const findLaborRateForJobType = useCallback(
    (jt) => {
      if (!jt) return 0;
      const fromMap = lookupLaborMapValue(laborHourlyRateByJobType, jt);
      if (fromMap > 0) return fromMap;

      const fromCatalog = lookupJobTypeCatalogRate(jobTypesCatalog, jt);
      if (fromCatalog > 0) return fromCatalog;

      const jtKey = normalizeJobTypeKey(jt);
      for (const group of categorizedFieldCopies) {
        for (const item of group?.items || []) {
          const itemJt = resolveFieldCopyDisplayJobType({
            jobType: item?.jobType || item?.type,
            formDataJobType: formData?.jobType,
          });
          if (normalizeJobTypeKey(itemJt) !== jtKey) continue;
          const rate = resolveFieldCopyCrewUnitRate(item);
          if (rate > 0) return rate;
          const row = getOfficeFieldCopyRowCalculations(item);
          if (row.displayPrice > 0) return row.displayPrice;
        }
      }

      for (const item of laborData || []) {
        const itemJt = resolveFieldCopyDisplayJobType({
          jobType: item?.jobType,
          formDataJobType: formData?.jobType,
        });
        if (normalizeJobTypeKey(itemJt) !== jtKey) continue;
        const rate = resolveFieldCopyCrewUnitRate(item);
        if (rate > 0) return rate;
      }

      const hrs = lookupLaborMapValue(laborManHoursByJobType, jt);
      for (const l of fieldLaborData || []) {
        const ljt = resolveFieldCopyDisplayJobType({
          jobType: l?.jobType,
          formDataJobType: formData?.jobType,
        });
        if (normalizeJobTypeKey(ljt) !== jtKey) continue;
        const total = Number(l?.totalPrice) || 0;
        if (total > 0 && hrs > 0) return total / hrs;
        const rate = resolveFieldCopyCrewUnitRate(l);
        if (rate > 0) return rate;
      }
      return 0;
    },
    [
      laborHourlyRateByJobType,
      laborManHoursByJobType,
      categorizedFieldCopies,
      laborData,
      fieldLaborData,
      formData?.jobType,
      jobTypesCatalog,
    ]
  );

  console.log("Data -----------", fieldLaborData, categorizedFieldCopies);

  const downloadPdf = () => {
    const element = document.getElementById("content-to-pdf");
    const pdfWrapper = element?.parentElement;

    const fileName = documentName + ".pdf";

    const options = {
      margin: 0.3,
      filename: fileName,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "A4", orientation: "portrait" },
      // allow table rows to continue on next page (avoid-all pushes whole table to page 2)
      pagebreak: { mode: ["css", "legacy"] },
    };

    const prevWrapperStyle = pdfWrapper?.getAttribute("style") || "";
    if (pdfWrapper) {
      pdfWrapper.setAttribute(
        "style",
        "position:fixed;left:-12000px;top:0;width:800px;display:block;visibility:visible;opacity:1;"
      );
    }

    html2pdf()
      .from(element)
      .set(options)
      // .save()
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        // Ensure we're on the last page
        const pageCount = pdf.internal.getNumberOfPages();
        pdf.setPage(pageCount);

        // Get page dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // console.log("Page details", pageCount, pageWidth, pageHeight);

        // Add footer text
        pdf.setFontSize(10);
        pdf.text(
          `


Approved by: __________________  Date: ____________________


Approved by: __________________  Date: ____________________`,
          pageWidth / 30,
          pageHeight - 1.2,
          { align: "left" }
        );

        // Save the PDF with the footer added
        pdf.save(fileName);
      })
      .catch((error) => {
        console.error("PDF generation failed:", error);
      })
      .finally(() => {
        if (pdfWrapper) {
          pdfWrapper.setAttribute("style", prevWrapperStyle || "display: none;");
        }
      });
  };

  // console.log("Labor Data", laborData)

  const buildCustomerCopyDisplayFieldCopies = useCallback(
    (copies) => {
      const base = Array.isArray(copies) ? copies : [];
      const wantDate = String(entryDate || "").trim();

      const officeLumpSumLines = [];
      const collectOfficeLumpSums = (entries) => {
        if (!Array.isArray(entries)) return;
        for (const entry of entries) {
          const entryKey = String(entry?.entryDate || "").trim();
          if (wantDate && entryKey && entryKey !== wantDate) continue;
          for (const fc of entry?.fieldCopies || []) {
            if (fc?.status === "Delete") continue;
            if (isLumpSumFieldCopySource(fc?.source)) {
              officeLumpSumLines.push(fc);
            }
          }
        }
      };

      collectOfficeLumpSums(formData?.officeFieldCopy);
      collectOfficeLumpSums(
        formData?.draftCopy?.map((d) => ({
          entryDate: d?.entryDate,
          fieldCopies: d?.draftCopies,
        }))
      );

      const lumpSumDedupeKey = (item) => {
        const baseRef =
          materialNameBaseForEdit(
            String(item?.reference || item?.referenceBase || ""),
            item?.vendorName
          )
            .trim()
            .toUpperCase() ||
          String(item?.type || item?.jobType || "")
            .trim()
            .toUpperCase();
        const typeKey = String(item?.type || item?.jobType || "")
          .trim()
          .toUpperCase();
        const taxable =
          item?.isTaxable === true ||
          item?.isTaxable === "true" ||
          String(item?.isTaxable || "")
            .trim()
            .toLowerCase() === "yes";
        return `${baseRef}|${typeKey}|${taxable}|${Number(item?.cost) || 0}|${Number(item?.totalPrice) || 0}`;
      };

      const existingKeys = new Set(
        base
          .filter((fc) => isLumpSumFieldCopySource(fc?.source))
          .map(lumpSumDedupeKey)
      );

      const supplemental = officeLumpSumLines.filter(
        (fc) => !existingKeys.has(lumpSumDedupeKey(fc))
      );
      if (!supplemental.length) return base;
      return [...base, ...supplemental];
    },
    [entryDate, formData?.draftCopy, formData?.officeFieldCopy]
  );

  useEffect(() => {
    if (
      fieldCopies.length > 0 ||
      materialData.length > 0 ||
      laborData.length > 0 ||
      formData?.officeFieldCopy?.length > 0
    ) {
      const displayCopies = buildCustomerCopyDisplayFieldCopies(fieldCopies);
      const summarizedData = summarizeFieldCopies(displayCopies).filter(
        (item) => !isLumpSumLaborFieldCopyItem(item)
      );

      setCategorizedFieldCopies([
        { category: "Materials & Other", items: summarizedData },
      ]);
      const { laborTotal, materialsTotal } = calculateTotals(displayCopies);
      setLaborTotal(laborTotal);
      setMaterialsTotal(materialsTotal);
    }
  }, [
    buildCustomerCopyDisplayFieldCopies,
    fieldCopies,
    formData?.officeFieldCopy,
    laborData.length,
    materialData.length,
  ]);

  useEffect(() => {
    getProjectById();
    getJobTypeById();
    getJobTypesCatalog();
    getTaxPercentage();
    getCustomerFieldCopyData();
    getFGAddress();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getJobTypeById();
  }, [formData]);

  const getProjectById = async () => {
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/project/get-project/${id}`,
        { headers: headers }
      );
      console.log("Server Project response", response.data.result);
      if (response.data.statusCode === 200) {
        if (!response.data.result.isProjectStarted) {
          navigate(-1);
        }
        setFormData(response.data.result);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.message);
    }
  };

  useEffect(() => {
    let doc_name = formData?.customerName || "";
    const completedDate = formData?.projectCompletedDate
      ? convertMillisecondsToDate(formData?.projectCompletedDate)
      : "";
    const salesOrderNumber = data?.uniqueId ?? "";
    const projectCode = formData?.projectCode;

    if (doc_name) {
      doc_name = (
        formData?.customerName?.toUpperCase() +
        (completedDate ? "-" : "") +
        completedDate +
        "-" +
        jobType +
        "-" +
        salesOrderNumber +
        "-" +
        projectCode
      )?.replace(" ", "");
    }
    setDocumentName(doc_name);
  }, [jobType, formData]);

  function moveLaborToBottom(data) {
    return data.sort((a, b) => {
      if (a.source === "Labor" && b.source !== "Labor") return 1;
      if (a.source !== "Labor" && b.source === "Labor") return -1;
      return 0;
    });
  }

  const getCustomerFieldCopyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/project/get-customer-copy/${id}/${entryDate}/${index}`,
        { headers: headers }
      );
      console.log("Server Customer response ", response.data.result);
      if (response.data.statusCode === 200) {
        const sortedCopies = moveLaborToBottom(
          response.data.result.customerCopiesData
        );
        let resultedCopies = [
          ...sortedCopies,
          // ...response.data.result.officeDraftCopies,
        ];

        let resultedMaterials = [];
        let fieldLabors = [];
        response.data.result.materialData.forEach((item) => {
          if (item.category !== "Labor") {
            resultedMaterials = [...resultedMaterials, item];
          } else {
            fieldLabors = [
              ...fieldLabors,
              {
                jobType: item.jobType,
                totalPrice: item.totalPrice,
                isLaborTaxable: item.isTaxable,
                dataType: "Labor",
              },
            ];
          }
        });
        resultedMaterials = categorizeMaterial(resultedMaterials);
        SetFieldLaborData(
          categorizeLabor(response.data.result.laborData || [])
        );
        let resultedLabors = [
          ...response.data.result.laborData,
          ...fieldLabors,
        ];
        resultedLabors = categorizeLabor(resultedLabors);
        // console.log("-----------------------", resultedCopies);

        setFieldCopies(resultedCopies);
        setMaterialData(resultedMaterials);
        setLaborData(resultedLabors);
        setMaterialLaborData(
          sortByJobType([...resultedMaterials, ...resultedLabors])
        );

        // setFieldCopies(response.data.result.customerCopiesData || []);
        // setMaterialData(response.data.result.materialData || []);
        // setLaborData(response.data.result.laborData || []);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.message);
    }
    setLoading(false);
  };

  function sortByJobType(data) {
    return data.sort((a, b) => a.jobType.localeCompare(b.jobType));
  }

  console.log("Material Labor Data", laborData);

  function lineItemCostSum(item) {
    const lineCost = getOfficeFieldCopyLineCost(item);
    if (lineCost > 0) return lineCost;
    if (
      item?.totalCost != null &&
      item.totalCost !== "" &&
      !Number.isNaN(Number(item.totalCost))
    ) {
      return Number(item.totalCost);
    }
    const c = item.cost;
    const q =
      item.quantity != null && item.quantity !== "" ? Number(item.quantity) : 1;
    if (c != null && c !== "" && Number(c) > 0) {
      return Number(c) * q;
    }
    return 0;
  }

  function categorizeLabor(laborData) {
    const categorizedData = laborData.reduce((result, item) => {
      const key = `${item.jobType}-${item.isLaborTaxable}`;

      if (!result[key]) {
        result[key] = {
          jobType: item.jobType,
          size: item?.size || item?.measure || "",
          totalPrice: 0,
          totalCost: 0,
          quantity: 0,
          cost: undefined,
          price: undefined,
          markup: undefined,
          markUp: undefined,
          isLaborTaxable: item.isLaborTaxable,
          dataType: item.dataType,
        };
      }
      result[key].totalPrice += item.totalPrice || 0;
      result[key].totalCost += lineItemCostSum(item);
      result[key].quantity +=
        item?.quantity != null && item?.quantity !== ""
          ? Number(item.quantity)
          : 0;
      if (
        result[key].cost === undefined &&
        item?.cost != null &&
        item?.cost !== ""
      ) {
        result[key].cost = Number(item.cost);
      }
      if (
        (!result[key].size || String(result[key].size).trim() === "") &&
        (item?.size || item?.measure)
      ) {
        result[key].size = item?.size || item?.measure;
      }
      if (
        result[key].price === undefined &&
        item?.price != null &&
        item?.price !== ""
      ) {
        result[key].price = Number(item.price);
      }
      if (
        result[key].markup === undefined &&
        item?.markup != null &&
        item?.markup !== ""
      ) {
        result[key].markup = Number(item.markup);
      }
      if (
        result[key].markUp === undefined &&
        item?.markUp != null &&
        item?.markUp !== ""
      ) {
        result[key].markUp = Number(item.markUp);
      }
      return result;
    }, {});

    return Object.values(categorizedData);
  }

  const formatQtyCell = (n) =>
    Number.isInteger(n)
      ? String(n)
      : n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const crewRowExistsForJobType = (jobTypeLabel) => {
    if (!jobTypeLabel) return false;
    const key = normalizeJobTypeKey(jobTypeLabel);
    return (fieldLaborData || []).some((labor) => {
      if (!(Number(labor?.totalPrice) !== 0)) return false;
      const jt = resolveFieldCopyDisplayJobType({
        jobType: labor?.jobType,
        formDataJobType: formData?.jobType,
      });
      return normalizeJobTypeKey(jt) === key;
    });
  };

  const getCustomerCopySourceLaborDisplayFields = (item) => {
    const storedTotal = Number(item?.totalPrice) || 0;
    let lineCost = Number(item?.totalCost) || 0;
    if (!(lineCost > 0) && storedTotal > 0) {
      const markupPct = parseFloat(item?.markup ?? item?.markUp);
      const m = Number.isFinite(markupPct) && markupPct > 0 ? markupPct : 100;
      lineCost = Math.round((storedTotal / (1 + m / 100)) * 100) / 100;
    }
    const displayTotal =
      storedTotal > 0
        ? storedTotal
        : lineCost > 0
          ? Math.round(lineCost * 2 * 100) / 100
          : 0;
    const markupVal = item?.markup ?? item?.markUp ?? null;
    return {
      lineCost,
      displayTotal,
      displayPrice: displayTotal > 0 ? displayTotal : null,
      markupVal,
      qtyText: "",
    };
  };

  const getPdfItemDisplayFields = (item) => {
    if (String(item?.source || "") === "Labor") {
      const laborDisplay = getCustomerCopySourceLaborDisplayFields(item);
      return {
        qty: 0,
        qtyDisplay: "",
        unitCostVal: laborDisplay.lineCost,
        displayPrice: laborDisplay.displayPrice,
        markupVal: snapCustomerCopyMarkupPercent(
          laborDisplay.markupVal,
          laborDisplay.lineCost,
          laborDisplay.displayTotal
        ),
        totalVal: laborDisplay.displayTotal,
      };
    }

    const {
      lineCost,
      displayPrice,
      markupVal,
      displayTotal,
      qtyText,
    } = getOfficeFieldCopyRowCalculations(item);
    const itemJt = resolveFieldCopyDisplayJobType({
      jobType: item?.jobType || item?.type,
      formDataJobType: formData?.jobType,
    });
    const crewHours = itemJt
      ? lookupLaborMapValue(laborManHoursByJobType, itemJt)
      : 0;

    const isLumpSum = isLumpSumFieldCopySource(item?.source);

    let qty = 0;
    let qtyDisplay = "";
    if (isLumpSum) {
      const qtyFromItem = Number(item?.quantity);
      if (Number.isFinite(qtyFromItem) && qtyFromItem > 0) {
        qty = qtyFromItem;
        qtyDisplay = formatQtyCell(qtyFromItem);
      }
    } else if (qtyText) {
      qtyDisplay = qtyText;
      qty = Number(qtyText) || 0;
    } else if (
      crewHours > 0 &&
      item?.source !== "Labor" &&
      !crewRowExistsForJobType(itemJt)
    ) {
      qty = crewHours;
      qtyDisplay = formatQtyCell(crewHours);
    } else {
      const qtyFromItem = Number(item?.quantity);
      if (Number.isFinite(qtyFromItem) && qtyFromItem > 0) {
        qty = qtyFromItem;
        qtyDisplay = formatQtyCell(qtyFromItem);
      }
    }
    return {
      qty,
      qtyDisplay,
      unitCostVal: lineCost,
      displayPrice,
      markupVal: snapCustomerCopyMarkupPercent(markupVal, lineCost, displayTotal),
      totalVal: displayTotal,
    };
  };

  const formatMoney = (n) =>
    Number(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  /** PDF $ — crisp render in html2pdf (avoid heavy faux-bold blur). */
  const pdfCurrencyDollarStyle = {
    display: "inline-block",
    fontWeight: 600,
    color: "#000000",
    lineHeight: 1,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
  };

  const pdfCurrencyAmountStyle = {
    display: "inline-block",
    fontVariantNumeric: "tabular-nums",
    color: "#000000",
    lineHeight: 1,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
  };

  /**
   * PDF table — $ sign + amount in a fixed-width box (space-between = gap).
   * boxWidth override: TOTAL column always uses 5rem; PRICE uses default below.
   */
  const renderCurrencyAmount = (
    n,
    { allowZero = false, boxWidth: boxWidthOverride } = {}
  ) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    if (!allowZero && num <= 0) return null;
    const amount = formatMoney(num);
    const boxWidth =
      boxWidthOverride ?? (amount.length > 9 ? "5rem" : "3rem");
    return (
      <span
        style={{
          display: "inline-flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          width: boxWidth,
          minWidth: boxWidth,
          whiteSpace: "nowrap",
        }}
      >
        <span style={pdfCurrencyDollarStyle}>$</span>
        <span style={pdfCurrencyAmountStyle}>{amount}</span>
      </span>
    );
  };

  /** PRICE column PDF — same box width (3rem / 5rem); $ fixed, amount grows left → right. */
  const renderPdfPriceCellContent = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    if (num <= 0) return null;
    const amount = formatMoney(num);
    const boxWidth = amount.length > 9 ? "5rem" : "3rem";
    return (
      <span
        style={{
          display: "inline-flex",
          justifyContent: "flex-start",
          alignItems: "baseline",
          gap: "1rem",
          width: boxWidth,
          minWidth: boxWidth,
          whiteSpace: "nowrap",
        }}
      >
        <span style={pdfCurrencyDollarStyle}>$</span>
        <span style={pdfCurrencyAmountStyle}>{amount}</span>
      </span>
    );
  };

  /** TOTAL column PDF — 4rem box ($ ↔ amount gap slightly tighter than before). */
  const renderPdfTotalCellContent = (n) =>
    renderCurrencyAmount(n, { boxWidth: "4rem" });

  const formatCurrencyDisplay = (n, alignRight = false) =>
    alignRight ? (
      renderCurrencyAmount(n, { allowZero: true, boxWidth: "4rem" })
    ) : (
      <span style={{ whiteSpace: "nowrap" }}>{`$ ${formatMoney(n)}`}</span>
    );

  /** Customer Copy — hide quantity for Source=Labor field-copy lines only. */
  const shouldHideCustomerCopySourceLaborQuantity = (item) =>
    String(item?.source || "").toLowerCase() === "labor";

  /** Customer Copy — Lump Sum has no qty on generate form; never show a forced 1. */
  const shouldHideCustomerCopyLumpSumQuantity = (item) =>
    isLumpSumFieldCopySource(item?.source);

  /** Materials & Other — merge labor rows when display description is exactly the same. */
  const customerCopyMergedTableRows = useMemo(() => {
    const entries = [];
    for (const item of categorizedFieldCopies?.[0]?.items ?? []) {
      entries.push({ kind: "item", item });
    }
    for (const labor of fieldLaborData || []) {
      if (!(Number(labor?.totalPrice) !== 0)) continue;
      const crewFields = getOfficeFieldCopyCrewLaborRowFields(
        labor,
        laborManHoursByJobType,
        laborHourlyRateByJobType,
        formData?.jobType,
        findLaborRateForJobType
      );
      if (!(crewFields.displayTotal > 0) && !(crewFields.lineCost > 0)) {
        continue;
      }
      entries.push({ kind: "crew", labor, crewFields });
    }

    const entryMergeItem = (entry) => {
      if (entry.kind === "crew") {
        return {
          dataType: "Labor",
          source: "Labor",
          jobType: entry.labor?.jobType,
          reference: entry.crewFields.description,
        };
      }
      return entry.item;
    };

    const entryCalc = (entry) => {
      if (entry.kind === "crew") {
        return customerCopyCrewTableCalc(entry.crewFields);
      }
      if (String(entry.item?.source || "") === "Labor") {
        return getCustomerCopySourceLaborDisplayFields(entry.item);
      }
      return getOfficeFieldCopyRowCalculations(entry.item);
    };

    const laborGroups = new Map();
    for (const entry of entries) {
      const mergeKey = getCustomerCopyMaterialsTableMergeKey(entryMergeItem(entry));
      if (!mergeKey) continue;

      const calc = entryCalc(entry);
      const description = getCustomerCopyMaterialsTableDisplayDescription(
        entryMergeItem(entry)
      );
      const size =
        entry.kind === "crew" ? entry.labor?.size || "" : entry.item?.size || "";
      const qtyN = calc.qtyText ? parseFloat(calc.qtyText) || 0 : 0;

      const entryIsSourceLabor =
        entry.kind === "item" &&
        shouldHideCustomerCopySourceLaborQuantity(entry.item);

      if (!laborGroups.has(mergeKey)) {
        laborGroups.set(mergeKey, {
          kind: "merged-labor",
          description,
          size,
          calc: { ...calc },
          qtySum: qtyN,
          hideSourceLaborQty: entryIsSourceLabor,
        });
      } else {
        const g = laborGroups.get(mergeKey);
        g.calc.lineCost =
          (Number(g.calc.lineCost) || 0) + (Number(calc.lineCost) || 0);
        g.calc.displayTotal =
          (Number(g.calc.displayTotal) || 0) + (Number(calc.displayTotal) || 0);
        g.qtySum = (g.qtySum || 0) + qtyN;
        if (!g.size && size) g.size = size;
        if (entryIsSourceLabor) g.hideSourceLaborQty = true;
      }
    }

    for (const g of laborGroups.values()) {
      const lc = Number(g.calc.lineCost) || 0;
      const dt = Number(g.calc.displayTotal) || 0;
      if (lc > 0 && dt > 0) {
        g.calc.markupVal = snapCustomerCopyMarkupPercent(g.calc.markupVal, lc, dt);
      }
      if (g.qtySum > 0) {
        g.calc.qtyText = Number.isInteger(g.qtySum)
          ? String(g.qtySum)
          : g.qtySum.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
        if (dt > 0) {
          g.calc.displayPrice = Math.round((dt / g.qtySum) * 100) / 100;
        }
      }
    }

    const seenKeys = new Set();
    const ordered = [];
    for (const entry of entries) {
      const mergeKey = getCustomerCopyMaterialsTableMergeKey(entryMergeItem(entry));
      if (!mergeKey) {
        ordered.push(entry);
        continue;
      }
      if (seenKeys.has(mergeKey)) continue;
      seenKeys.add(mergeKey);
      const merged = laborGroups.get(mergeKey);
      if (merged) ordered.push(merged);
    }
    return ordered;
  }, [
    categorizedFieldCopies,
    fieldLaborData,
    formData?.jobType,
    laborManHoursByJobType,
    laborHourlyRateByJobType,
    findLaborRateForJobType,
  ]);

  /** Customer Copy view + PDF — Lump Sum: DB reference, strip vendor suffix only when appended at end. */
  const getCustomerCopyPdfLumpSumTableDescription = useCallback(
    (item) => {
      const stripVendorSuffixOnly = (storedReference, vendorName) => {
        const ref = String(storedReference ?? "").trim();
        if (!ref) return "";
        const vendor = String(vendorName ?? "").trim();
        if (vendor) {
          const suffix = ` (${vendor})`;
          if (ref.endsWith(suffix)) {
            return ref.slice(0, -suffix.length).trim();
          }
        }
        return ref;
      };

      const sell = Number(item?.totalPrice) || 0;
      const typeKey = String(item?.type || item?.jobType || "")
        .trim()
        .toUpperCase();
      const taxable = !!item?.isTaxable;

      const lineMatches = (fc) => {
        if (!isLumpSumFieldCopySource(fc?.source)) return false;
        const fcType = String(fc?.type || fc?.jobType || "")
          .trim()
          .toUpperCase();
        if (typeKey && fcType !== typeKey) return false;
        if (!!fc?.isTaxable !== taxable) return false;
        if (sell > 0 && Math.abs(Number(fc?.totalPrice) - sell) > 0.02) {
          return false;
        }
        return true;
      };

      const candidates = [];
      const collectFrom = (list) => {
        for (const fc of list || []) {
          if (!lineMatches(fc)) continue;
          const ref = String(fc?.reference || fc?.referenceBase || "").trim();
          if (!ref) continue;
          candidates.push(
            stripVendorSuffixOnly(ref, fc?.vendorName || fc?.vendor)
          );
        }
      };

      collectFrom(buildCustomerCopyDisplayFieldCopies(fieldCopies));

      const wantDate = String(entryDate || "").trim();
      for (const entry of formData?.officeFieldCopy || []) {
        if (wantDate && String(entry?.entryDate || "").trim() !== wantDate) {
          continue;
        }
        collectFrom(entry?.fieldCopies);
      }

      if (candidates.length > 0) {
        const best = candidates.reduce((a, b) => (a.length >= b.length ? a : b));
        return best.toUpperCase();
      }

      const ref = String(item?.reference || item?.referenceBase || "").trim();
      if (ref) {
        return stripVendorSuffixOnly(ref, item?.vendorName).toUpperCase();
      }
      return getCustomerCopyDisplayDescription(item);
    },
    [
      fieldCopies,
      buildCustomerCopyDisplayFieldCopies,
      entryDate,
      formData?.officeFieldCopy,
    ]
  );

  const renderCustomerCopyTableBody = (variant = "view") => {
    const isPdf = variant === "pdf";
    const pdfMoneyCellStyle = {
      textAlign: "right",
      whiteSpace: "nowrap",
      paddingRight: "2px",
      verticalAlign: "top",
    };
    const pdfPriceCellStyle = {
      textAlign: "right",
      whiteSpace: "nowrap",
      paddingLeft: "0px",
      paddingRight: "16px",
      verticalAlign: "top",
    };
    const pdfDescCellStyle = {
      textAlign: "left",
      verticalAlign: "top",
      lineHeight: "1.35",
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      letterSpacing: "normal",
      paddingTop: "2px",
      paddingBottom: "2px",
      paddingRight: "4px",
    };
    return (
      <>
        {customerCopyMergedTableRows.map((row, idx) => {
          if (row.kind === "merged-labor") {
            const d = row.calc;
            const hideLaborQty = !!row.hideSourceLaborQty;
            const hidePdfLaborFields = isPdf;
            const pdfQtyValue =
              !hidePdfLaborFields && !hideLaborQty ? d.qtyText : "";
            return (
              <tr
                key={`${variant}-merged-labor-${idx}`}
                style={
                  isPdf
                    ? { pageBreakInside: "avoid", breakInside: "avoid" }
                    : undefined
                }
              >
                <td
                  className={isPdf ? "text-xs pr-2" : "w-[400px] pr-2"}
                  style={isPdf ? pdfDescCellStyle : undefined}
                >
                  {isPdf ? row.description : <p className="m-0">{row.description}</p>}
                </td>
                <td
                  className={isPdf ? "text-xs" : undefined}
                  style={isPdf ? getPdfSizeCellStyle(row.size) : undefined}
                >
                  {isPdf ? formatPdfSizeDisplay(row.size) : row.size || ""}
                </td>
                <td
                  className={isPdf ? "text-xs" : undefined}
                  style={isPdf ? getPdfQtyCellStyle(pdfQtyValue) : undefined}
                >
                  {isPdf
                    ? formatPdfQtyDisplay(pdfQtyValue)
                    : !hidePdfLaborFields && !hideLaborQty && d.qtyText
                      ? d.qtyText
                      : ""}
                </td>
                {!isPdf && (
                  <td>{d.lineCost > 0 ? formatMoney(d.lineCost) : ""}</td>
                )}
                {!isPdf && (
                  <td>
                    {formatCustomerCopyMarkupCell(
                      d.markupVal,
                      d.lineCost,
                      d.displayTotal
                    )}
                  </td>
                )}
                {isPdf ? (
                  <>
                    <td className="text-xs" style={pdfPriceCellStyle} />
                    <td className="text-xs" style={pdfMoneyCellStyle}>
                      {renderPdfTotalCellContent(d.displayTotal)}
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {d.displayPrice != null && d.displayPrice > 0
                        ? formatMoney(d.displayPrice)
                        : ""}
                    </td>
                    <td>
                      {d.displayTotal > 0 ? formatMoney(d.displayTotal) : ""}
                    </td>
                  </>
                )}
              </tr>
            );
          }

          const item = row.item;
          const d = getPdfItemDisplayFields(item);
          const hideLaborQty = shouldHideCustomerCopySourceLaborQuantity(item);
          const hideLumpSumQty = shouldHideCustomerCopyLumpSumQuantity(item);
          const hidePdfLaborFields =
            isPdf && String(item?.source || "").toLowerCase() === "labor";
          const hidePdfLumpSumPrice =
            isPdf && isLumpSumFieldCopySource(item?.source);
          const pdfQtyValue =
            !hidePdfLaborFields && !hideLaborQty && !hideLumpSumQty
              ? d.qtyDisplay || (d.qty > 0 ? formatQtyCell(d.qty) : "")
              : "";
          return (
            <tr
              key={`${variant}-item-${idx}`}
              style={
                isPdf
                  ? { pageBreakInside: "avoid", breakInside: "avoid" }
                  : undefined
              }
            >
              <td
                className={isPdf ? "text-xs pr-2" : "w-[400px] pr-2"}
                style={isPdf ? pdfDescCellStyle : undefined}
              >
                {isLumpSumFieldCopySource(item?.source)
                  ? getCustomerCopyPdfLumpSumTableDescription(item)
                  : getCustomerCopyDisplayDescription(item)}
              </td>
              <td
                className={isPdf ? "text-xs" : undefined}
                style={isPdf ? getPdfSizeCellStyle(item?.size) : undefined}
              >
                {isPdf ? formatPdfSizeDisplay(item?.size) : item?.size || ""}
              </td>
              <td
                className={isPdf ? "text-xs" : undefined}
                style={isPdf ? getPdfQtyCellStyle(pdfQtyValue) : undefined}
              >
                {isPdf
                  ? formatPdfQtyDisplay(pdfQtyValue)
                  : !hidePdfLaborFields &&
                      !hideLaborQty &&
                      !hideLumpSumQty &&
                      (d.qtyDisplay || (d.qty > 0 ? formatQtyCell(d.qty) : ""))}
              </td>
              {!isPdf && (
                <td>
                  {d.unitCostVal > 0 ? formatMoney(d.unitCostVal) : ""}
                </td>
              )}
              {!isPdf && (
                <td>
                  {formatCustomerCopyMarkupCell(
                    d.markupVal,
                    d.unitCostVal,
                    d.totalVal
                  )}
                </td>
              )}
              <td
                className={isPdf ? "text-xs" : undefined}
                style={isPdf ? pdfPriceCellStyle : undefined}
              >
                {!hidePdfLaborFields &&
                !hidePdfLumpSumPrice &&
                d.displayPrice != null &&
                !Number.isNaN(d.displayPrice) &&
                d.displayPrice > 0
                  ? isPdf
                    ? renderPdfPriceCellContent(d.displayPrice)
                    : formatMoney(d.displayPrice)
                  : ""}
              </td>
              <td
                className={isPdf ? "text-xs" : undefined}
                style={isPdf ? pdfMoneyCellStyle : undefined}
              >
                {d.totalVal > 0
                  ? isPdf
                    ? renderPdfTotalCellContent(d.totalVal)
                    : formatMoney(d.totalVal)
                  : ""}
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  const renderCustomerCopyJobTypeSummary = (compact = false) =>
    customerCopyInvoiceSummaryRows.map((item, idx) => {
      if (item.dataType === "Material") {
        const sourceLaborLabel =
          item.source === "Labor" && item.reference
            ? getCustomerCopyDisplayDescription(item)
            : null;
        return (
          <div
            key={`summary-mat-${idx}`}
            className={compact ? "flex justify-between mt-1" : "flex justify-between mt-1 capitalize"}
            style={
              compact
                ? { pageBreakInside: "avoid", breakInside: "avoid" }
                : undefined
            }
          >
            <span>
              <b
                className={compact ? "w-[200px] inline-block" : undefined}
                style={compact ? { whiteSpace: "pre" } : { whiteSpace: "pre" }}
              >
                {sourceLaborLabel ? (
                  <>{sourceLaborLabel.toUpperCase()}</>
                ) : item.source === "Labor" ? (
                  <>{item.jobType?.toUpperCase()} LABOR</>
                ) : isLumpSumFieldCopySource(item.source) ? (
                  <>
                    {String(item.type || item.jobType || "").toLowerCase().includes("lodging")
                      ? "LUMP SUM"
                      : String(item.type || item.jobType || "Lump Sum")
                          .replace(/\s*\([^)]*sales tax paid[^)]*\)/i, "")
                          .trim()
                          .toUpperCase()}
                  </>
                ) : item.jobType?.toLowerCase() === "equipment fees" ? (
                  "EQUIPMENT LUMP SUM"
                ) : (
                  <>
                    {item.jobType?.toUpperCase()}{" "}
                    {handleInvoiceJobType(item.jobType)}
                  </>
                )}
              </b>
              {compact && (
                <b>
                  {formData?.customerType === "Normal"
                    ? ["Labor", "Other"].includes(item.source)
                      ? item.isTaxable
                        ? "RT"
                        : "RNT"
                      : isLumpSumFieldCopySource(item.source)
                        ? `${item.isTaxable ? "RT" : "RNT"} ${
                            String(item.type || item.jobType || "").toLowerCase().includes("lodging")
                              ? "(SALES TAX PAID ON LODGING)"
                              : "(SALES TAX PAID ON MATERIAL)"
                          }`
                        : "RT"
                    : formData.customerType === "Commercial"
                      ? "CT"
                      : formData?.customerType?.toUpperCase()}
                </b>
              )}
            </span>
            <span className={compact ? "shrink-0 ml-auto" : undefined}>
              {compact
                ? formatCurrencyDisplay(item.totalPrice, true)
                : formatCurrencyDisplay(item.totalPrice)}
            </span>
          </div>
        );
      }
      if (item.totalPrice > 0) {
        return (
          <div
            key={`summary-lab-${idx}`}
            className={compact ? "flex justify-between mt-1" : "flex justify-between mt-1 capitalize"}
            style={
              compact
                ? { pageBreakInside: "avoid", breakInside: "avoid" }
                : undefined
            }
          >
            <span>
              <b className={compact ? "w-[200px] inline-block" : undefined}>
                {item.jobType?.toUpperCase()} LABOR
              </b>
              {compact && (
                <b>
                  {formData?.customerType === "Normal"
                    ? item.isLaborTaxable ?? item.isTaxable
                      ? "RT"
                      : "RNT"
                    : formData.customerType === "Commercial"
                      ? "CT"
                      : formData?.customerType?.toUpperCase()}
                </b>
              )}
            </span>
            <span className={compact ? "shrink-0 ml-auto" : undefined}>
              {compact
                ? formatCurrencyDisplay(item.totalPrice, true)
                : formatCurrencyDisplay(item.totalPrice)}
            </span>
          </div>
        );
      }
      return null;
    });

  const renderCustomerCopyFuelSurcharge = (compact = false) => {
    if (!isFuelSurchargeEnabledOnProject(formData)) return null;
    return (
      <div
        className="flex justify-between mt-1"
        style={
          compact
            ? { pageBreakInside: "avoid", breakInside: "avoid" }
            : undefined
        }
      >
        <span>
          <b
            className={compact ? "w-[200px] inline-block" : undefined}
            style={{ whiteSpace: "pre" }}
          >
            FUEL SURCHARGE
          </b>
          {compact && (
            <b>{formData?.customerType === "Commercial" ? "CT" : "RT"}</b>
          )}
        </span>
        <span className={compact ? "shrink-0 ml-auto" : undefined}>
          {compact
            ? formatCurrencyDisplay(customerCopyFuelSurcharge, true)
            : formatCurrencyDisplay(customerCopyFuelSurcharge)}
        </span>
      </div>
    );
  };

  // function categorizeMaterial(materialData) {
  //   const categorizedData = materialData.reduce((result, item) => {
  //     // Check if the jobType already exists in the result object
  //     if (!result[item.jobType]) {
  //       // Initialize a new entry for this jobType
  //       result[item.jobType] = {
  //         jobType: item.jobType,
  //         totalPrice: 0,
  //         isTaxable: item.isTaxable,
  //         source: item.source,
  //       };
  //     }
  //     // Sum up the totalPrice for the current jobType
  //     result[item.jobType].totalPrice += item.totalPrice;
  //     return result;
  //   }, {});

  //   // Convert the result object to an array format
  //   return Object.values(categorizedData);
  // }

  function categorizeMaterial(materialData) {
    const categorizedData = materialData.reduce((result, item) => {
      const category = item.source === "Labor" ? "Labor" : "F&G/Other/LumpSum";
      const reference = String(item?.reference || "").trim();
      const isLumpSum = isLumpSumFieldCopySource(item?.source);
      // Invoice Summary: same Lump Sum description + same taxable → one line (sum totals); no vendor in label/key.
      const lumpSumBaseRef = isLumpSum
        ? materialNameBaseForEdit(reference, item.vendorName).trim() ||
          materialNameBaseForEdit(reference, "").trim() ||
          reference
        : "";
      const isLodging =
        isLumpSum &&
        String(item?.type || item?.jobType || "").toLowerCase().includes("lodging");
      const lodgingJobType = String(item?.jobType || item?.type || "").trim().toUpperCase();
      const lodgingSource = String(item?.source || "").trim().toUpperCase();

      const key =
        item.source === "Labor" && reference
          ? `${category}_${reference}_${item.jobType}_${item.isTaxable}`
          : isLodging
            ? `${category}_LODGING_${lodgingSource}_${lodgingJobType}_${!!item.isTaxable}`
            : isLumpSum
              ? `${category}_LUMP_${lumpSumBaseRef.toUpperCase()}_${!!item.isTaxable}`
              : `${category}_${item.jobType}_${item.isTaxable}`;

      if (!result[key]) {
        result[key] = {
          category,
          jobType: item.jobType,
          type: item.type || item.jobType,
          reference: isLumpSum ? lumpSumBaseRef : reference,
          totalPrice: 0,
          isTaxable: item.isTaxable,
          isLaborTaxable: item.isLaborTaxable,
          source: item.source,
          dataType: item.dataType,

          cost: 0,
          markup: 0,
          quantity: 0,
        };
      }

      const qty =
        isLumpSum
          ? Number(item.quantity) > 0
            ? Number(item.quantity)
            : 0
          : item.quantity || 1;

      result[key].totalPrice += item.totalPrice || 0;
      result[key].quantity += qty;
      if (item.isTaxable) result[key].isTaxable = true;
      if (item.isLaborTaxable) result[key].isLaborTaxable = true;
      if (!isLumpSum && !result[key].reference && reference) {
        result[key].reference = reference;
      }

      if (item.cost) {
        result[key].cost += Number(item.cost) * (qty > 0 ? qty : 1);
      }

      if (item.markUp || item.markup) {
        result[key].markup += Number(item.markUp || item.markup);
      }

      return result;
    }, {});

    return Object.values(categorizedData);
  }

  const jobTypeForCustomerCopyTableRow = (row) => {
    if (row?.jobType || row?.type) return row.jobType || row.type;
    const refKey = String(row?.reference || "").trim().toUpperCase();
    for (const fc of fieldCopies || []) {
      const fcRef = String(fc?.reference || "").trim().toUpperCase();
      if (String(fc?.source || "").toLowerCase() === "labor") {
        if (refKey && fcRef === refKey) return fc.jobType || fc.type;
        continue;
      }
      const base = materialNameBaseForEdit(
        String(fc.reference || ""),
        fc.vendorName
      )
        .trim()
        .toUpperCase();
      if (refKey && base === refKey) return fc.jobType || fc.type;
    }
    if (refKey.includes("HARDSCAPE")) return "Hardscape";
    return formData?.jobType;
  };

  /** Invoice summary rows — rebuilt from Materials & Other table totals (not API materialData). */
  const customerCopyInvoiceSummaryRows = useMemo(() => {
    const tableItems = categorizedFieldCopies?.[0]?.items ?? [];

    const laborTaxLookup = {
      materialData,
      officeFieldCopy: formData?.officeFieldCopy,
      draftCopy: formData?.draftCopy,
      jobTypesCatalog,
    };

    const materialSeeds = tableItems.map((row) => {
      const isSourceLabor = String(row?.source || "") === "Labor";
      const { displayTotal } = isSourceLabor
        ? getCustomerCopySourceLaborDisplayFields(row)
        : getOfficeFieldCopyRowCalculations(row);
      const sell =
        Number(displayTotal) > 0
          ? displayTotal
          : Number(row?.totalPrice) || 0;
      const jobType = jobTypeForCustomerCopyTableRow(row);
      const lineTaxable =
        row?.isTaxable === true ||
        row?.isTaxable === "true" ||
        String(row?.isTaxable || "")
          .trim()
          .toLowerCase() === "yes";
      return {
        jobType,
        type: row?.type || jobType,
        reference: row?.reference,
        source: row?.source,
        isTaxable: isSourceLabor ? lineTaxable : row?.isTaxable,
        isLaborTaxable: isSourceLabor ? lineTaxable : undefined,
        dataType: "Material",
        totalPrice: sell,
        quantity: row?.quantity,
        cost: row?.cost,
        markup: row?.markup ?? row?.markUp,
      };
    });

    const laborSeeds = (fieldLaborData || [])
      .filter((labor) => Number(labor?.totalPrice) !== 0)
      .map((labor) => {
        const crewRow = getOfficeFieldCopyCrewLaborRowFields(
          labor,
          laborManHoursByJobType,
          laborHourlyRateByJobType,
          formData?.jobType,
          findLaborRateForJobType
        );
        const sell =
          Number(crewRow?.displayTotal) > 0
            ? crewRow.displayTotal
            : Number(labor?.totalPrice) || 0;
        return {
          jobType: labor?.jobType,
          totalPrice: sell,
          isLaborTaxable: resolveCustomerCopyCrewLaborTaxable(
            labor,
            fieldCopies,
            formData?.jobType,
            laborTaxLookup
          ),
          dataType: "Labor",
        };
      });

    return sortByJobType(
      mergeCustomerCopyInvoiceSummaryRows([
        ...categorizeMaterial(materialSeeds),
        ...categorizeLabor(laborSeeds),
      ])
    );
  }, [
    categorizedFieldCopies,
    fieldCopies,
    fieldLaborData,
    formData?.jobType,
    formData?.officeFieldCopy,
    formData?.draftCopy,
    materialData,
    laborManHoursByJobType,
    laborHourlyRateByJobType,
    findLaborRateForJobType,
    jobTypesCatalog,
  ]);

  const customerCopyInvoiceGrossSubtotal = useMemo(
    () =>
      (customerCopyInvoiceSummaryRows || []).reduce(
        (acc, item) => acc + (Number(item?.totalPrice) || 0),
        0
      ),
    [customerCopyInvoiceSummaryRows]
  );

  /** Project toggle ON → percent of material lines. Labor / Lump Sum excluded. Always taxable. */
  const customerCopyFuelSurcharge = useMemo(
    () => calcProjectFuelSurcharge(customerCopyInvoiceSummaryRows, formData),
    [
      customerCopyInvoiceSummaryRows,
      formData?.isFuelSurchargeEnabled,
      formData?.fuelSurchargePercent,
    ]
  );

  const customerCopyInvoiceNetSubtotal = useMemo(() => {
    const tc = Number(formData?.taxCredits) || 0;
    const ntc = Number(formData?.nonTaxCredits) || 0;
    return customerCopyInvoiceGrossSubtotal - tc - ntc + customerCopyFuelSurcharge;
  }, [
    customerCopyInvoiceGrossSubtotal,
    customerCopyFuelSurcharge,
    formData?.taxCredits,
    formData?.nonTaxCredits,
  ]);

  const customerCopySalesTax = useMemo(() => {
    const tc = Number(formData?.taxCredits) || 0;
    const base = formData?.isProjectTaxable
      ? Math.max(0, Number(taxableAmount) - tc)
      : tc > Number(taxableAmount)
        ? 0
        : Number(taxableAmount);
    return (Number(taxPercent) * (base + customerCopyFuelSurcharge)) / 100;
  }, [
    formData?.isProjectTaxable,
    formData?.taxCredits,
    taxableAmount,
    taxPercent,
    customerCopyFuelSurcharge,
  ]);

  const customerCopyGrandTotal = useMemo(
    () => customerCopyInvoiceNetSubtotal + customerCopySalesTax,
    [customerCopyInvoiceNetSubtotal, customerCopySalesTax]
  );

  useEffect(() => {
    if (formData.customerType === "Normal") {
      const taxAmount = (customerCopyInvoiceSummaryRows || []).reduce(
        (acc, item) => {
          if (!item) return acc;
          if (item.dataType === "Material") {
            if (!item.isTaxable) return acc;
          } else if (!(item.isLaborTaxable ?? item.isTaxable)) {
            return acc;
          }
          return acc + Number(item.totalPrice || 0);
        },
        0
      );
      setTaxableAmount(Number.parseFloat(taxAmount));
    } else if (formData.customerType === "Commercial") {
      setTaxableAmount(materialsTotal + laborTotal);
    } else if (formData.customerType === "Exempt") {
      setTaxableAmount(0);
    }
  }, [
    formData?.customerType,
    laborTotal,
    customerCopyInvoiceSummaryRows,
    materialsTotal,
    jobTypesCatalog,
  ]);

  // function categorizeMaterial(materialData) {
  //   const categorizedData = materialData.reduce((result, item) => {
  //     // Determine the category based on source
  //     const category = item.source === "Labor" ? "Labor" : "F&G/Other/LumpSum";

  //     // Create a unique key using category & jobType
  //     const key = `${category}_${item.jobType}_${item.isTaxable}`;

  //     // Check if the category and jobType already exist in the result object
  //     if (!result[key]) {
  //       result[key] = {
  //         category, // Store the category
  //         jobType: item.jobType,
  //         totalPrice: 0,
  //         isTaxable: item.isTaxable,
  //         source: item.source,
  //         dataType: item.dataType,

  //       };
  //     }

  //     // Sum up the totalPrice for the current category & jobType
  //     result[key].totalPrice += item.totalPrice;

  //     return result;
  //   }, {});

  //   // Convert the result object to an array format
  //   return Object.values(categorizedData);
  // }

  const getJobTypeById = async () => {
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      if (!formData.jobType) {
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/project/get-job-type/${formData.jobType}`,
        { headers: headers }
      );
      // console.log("abhinandan",response)
      if (response.data.statusCode === 200) {
        setJobType(response.data.result.jobName);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.message);
    }
  };

  const getJobTypesCatalog = async () => {
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get-job-types-dpd`,
        { headers }
      );
      if (response.data.statusCode === 200) {
        setJobTypesCatalog(response.data.result || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getTaxPercentage = async () => {
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/staff/get-tax-percent`,
        { headers: headers }
      );
      console.log("Server Tax Percentage response ", response.data.result);
      if (response.data.statusCode === 200) {
        setTaxPercent(response.data.result.taxPercent);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.message);
    }
  };

  const calculateTotals = (fieldCopies) => {
    const totals = {
      laborTotal: 0,
      materialsTotal: 0,
    };

    materialData.forEach((item) => {
      totals.materialsTotal += item.totalPrice;
    });

    laborData.forEach((item) => {
      totals.laborTotal += item.totalPrice;
    });

    return totals;
  };

  // const summarizeFieldCopies = (fieldCopies) => {
  //   const summary = {};

  //   fieldCopies.forEach((item) => {
  //     const key = `${item.reference}-${item.measure}-${item.price}`;

  //     if (!summary[key]) {
  //       summary[key] = {
  //         source: item.source,
  //         isTaxable: item.isTaxable,
  //         reference: item.reference,
  //         description: item.description,
  //         size: item.measure,
  //         quantity: 0,
  //         price: item.price,
  //         totalPrice: 0,
  //         cost: item.cost || 0,
  //         markup: item.markUp || item.markup || 0,
  //       };
  //     }

  //     summary[key].quantity += item.quantity;
  //     summary[key].totalPrice += item.totalPrice;
  //   });

  //   // Calculate the total price
  //   // Object.keys(summary).forEach((key) => {
  //   //   summary[key].totalPrice = summary[key].quantity * summary[key].price;
  //   // });

  //   return Object.values(summary);
  // };

  const summarizeFieldCopies = (fieldCopies) => {
    const summary = {};
    const num = (v) => {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    fieldCopies.forEach((item) => {
      if (item.source === "Labor") {
        const key = `${item.reference}-${item.measure}-${item.price}`;
        if (!summary[key]) {
          summary[key] = {
            source: item.source,
            isTaxable: item.isTaxable,
            reference: item.reference,
            vendorName: item.vendorName || item.vendor || "",
            size: item.measure,
            quantity: 1,
            price: 0,
            cost: 0,
            totalCost: 0,
            markup: 0,
            markUp: 0,
            totalPrice: 0,
          };
        }
        const lineQty = num(item.quantity) > 0 ? num(item.quantity) : 1;
        summary[key].totalPrice += num(item.totalPrice);
        summary[key].isTaxable = summary[key].isTaxable || item.isTaxable;
        const unitCost = num(item.cost);
        if (unitCost > 0) {
          summary[key].totalCost =
            (Number(summary[key].totalCost) || 0) + unitCost * lineQty;
        } else {
          const sell = num(item.totalPrice);
          if (sell > 0) {
            const m =
              num(item.markUp ?? item.markup) > 0
                ? num(item.markUp ?? item.markup)
                : 100;
            summary[key].totalCost =
              (Number(summary[key].totalCost) || 0) +
              Math.round((sell / (1 + m / 100)) * 100) / 100;
          }
        }
        if (
          (summary[key].markup === 0 || summary[key].markup === undefined) &&
          (item.markUp != null || item.markup != null)
        ) {
          summary[key].markup = Number(item.markUp ?? item.markup);
          summary[key].markUp = summary[key].markup;
        }
        return;
      }

      const isLumpSum = isLumpSumFieldCopySource(item.source);
      const baseMaterialName =
        materialNameBaseForEdit(
          String(item.reference || item.referenceBase || ""),
          item.vendorName
        ).trim() ||
        (isLumpSum ? String(item.type || item.jobType || "Lump Sum").trim() : "");
      const materialKey = baseMaterialName.toUpperCase();
      if (!materialKey) return;

      const vendorName = String(item.vendorName || item.vendor || "").trim();
      // Lump Sum (View only): merge same base description + taxable; Edit keeps separate rows in DB.
      const key = isLumpSum
        ? `${String(item.source || "").trim()}-${materialKey}-${!!item.isTaxable}`
        : `${String(item.source || "").trim()}-${materialKey}`;

      const qty = num(item.quantity);
      const unitPrice = num(item.price);
      const unitCost =
        item.cost !== undefined && item.cost !== null && item.cost !== ""
          ? num(item.cost)
          : 0;
      const markupRaw = item.markUp ?? item.markup;
      const lineTotal = num(item.totalPrice);
      const lineCostTotal = unitCost > 0 ? unitCost : 0;

      if (!summary[key]) {
        summary[key] = {
          source: isLumpSum ? "Lump Sum" : item.source,
          isTaxable: item.isTaxable,
          reference: baseMaterialName,
          type: item.type || item.jobType,
          jobType: item.type || item.jobType,
          vendorName: isLumpSum ? "" : vendorName,
          size: item.measure,
          quantity: 0,
          price: 0,
          cost: 0,
          markup: 0,
          markUp: 0,
          totalPrice: 0,
        };
      }

      const row = summary[key];
      row.quantity += qty;
      row.totalPrice += lineTotal;
      row.cost = (Number(row.cost) || 0) + lineCostTotal;

      if (unitPrice > row.price) {
        row.price = unitPrice;
      }

      if (
        (row.markup === 0 || row.markup === undefined) &&
        markupRaw != null &&
        markupRaw !== ""
      ) {
        row.markup = num(markupRaw);
        row.markUp = row.markup;
      }

      row.isTaxable = row.isTaxable || item.isTaxable;
      if (!row.size && item.measure) {
        row.size = item.measure;
      }
      if (!isLumpSum && vendorName && !row.vendorName) {
        row.vendorName = vendorName;
      }
      if (isLumpSum) {
        const mergedCost = Number(row.cost) || 0;
        const mergedTotal = Number(row.totalPrice) || 0;
        if (mergedCost > 0 && mergedTotal > 0) {
          const mergedMarkup =
            Math.round(((mergedTotal - mergedCost) / mergedCost) * 10000) / 100;
          row.markUp = mergedMarkup;
          row.markup = mergedMarkup;
        }
      }
    });

    const finalizeMaterialSummaryRow = (row) => {
      const isLumpSum = isLumpSumFieldCopySource(row.source);
      const markupPct = parseFloat(row.markup ?? row.markUp) || 0;
      let qty = num(row.quantity);
      // Lump Sum: never invent quantity 1 (generate form has no qty field).
      const qtySafe = qty > 0 ? qty : isLumpSum ? 0 : 1;
      const unitCost = num(row.cost);
      let unitPrice = num(row.price);
      let totalPrice = num(row.totalPrice);

      if (!(unitPrice > 0) && unitCost > 0) {
        if (markupPct > 0) {
          unitPrice =
            Math.round(unitCost * (1 + markupPct / 100) * 10000) / 10000;
        } else if (row.source === "Other") {
          unitPrice = Math.round(unitCost * 2 * 10000) / 10000;
        }
      }

      if (!(totalPrice > 0)) {
        if (unitPrice > 0) {
          totalPrice =
            Math.round(unitPrice * (qtySafe > 0 ? qtySafe : 1) * 100) / 100;
        } else if (unitCost > 0 && markupPct > 0) {
          totalPrice =
            Math.round(
              (unitCost + (unitCost * markupPct) / 100) *
                (qtySafe > 0 ? qtySafe : 1) *
                100
            ) / 100;
        }
      }

      // Lump Sum: PRICE column = line total (not a unit × forced qty).
      if (isLumpSum && totalPrice > 0) {
        unitPrice = totalPrice;
      }

      return {
        ...row,
        quantity: qty > 0 ? qty : isLumpSum ? 0 : qtySafe,
        cost: unitCost,
        price: unitPrice,
        totalPrice,
        markup: markupPct,
        markUp: markupPct,
      };
    };

    return Object.values(summary).map((row) => {
      if (row.source === "Labor") {
        return finalizeLaborSummaryRow(row);
      }
      return finalizeMaterialSummaryRow(row);
    });
  };

  const getFGAddress = async () => {
    try {
      const token = localStorage.getItem("f&gstafftoken");
      const headers = {
        token: token,
        "Content-Type": "application/json",
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get-fg-address`,
        { headers: headers }
      );
      console.log("Server F&G Address response ", response.data.result);
      if (response.data.statusCode === 200) {
        setAddress(response.data.result);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.message);
    }
  };

  function formatDate(dateInput) {
    try {
      if (!dateInput) {
        return;
      }
      dateInput = Number.parseInt(dateInput, 10);
      const date = new Date(dateInput);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date input");
      }

      const months = [
        "January",
        "Febuary",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      console.log("Error", error);
    }
  }

  const saveDocumentAs = () => {
    if (!documentName) {
      toast.error("Please enter document name.");
      return;
    }
    downloadPdf();
  };

  const updateDocumentName = (val) => {
    val = val?.replace(" ", "_");
    setDocumentName(val);
  };

  function convertToCentralTime(milliseconds) {
    // Convert milliseconds to a Date object

    milliseconds = Number.parseInt(milliseconds);

    const date = new Date(milliseconds);

    // Format the date to Central Time (US & Canada)
    let centralTime = date.toLocaleString("en-US", {
      // timeZone: "America/Chicago", // Central Time Zone
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return centralTime;
  }

  function convertMillisecondsToDate(ms) {
    ms = Number.parseInt(ms);
    const date = new Date(ms);

    // Add 1 day
    date.setDate(date.getDate() + 1);

    // Extract month, day, and year
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-4); // Get the last 2 digits of the year

    // Return in MM/DD/YY format
    return `${month}/${day}/${year}`;
  }

  // const handleInvoiceJobType = (jobType) => {
  //   if (["professional", "pick up/delivery"].includes(jobType.toLowerCase())) {
  //     return "SERVICE";
  //   } else if (jobType.toLowerCase() === "equipment rental") {
  //     return "";
  //   } else if (jobType.toLowerCase() === "reimbursement total") {
  //     return "";
  //   }else if (jobType.includes("Lump Sum")) {
  //     return "";
  //   } else {
  //     return "MATERIAL";
  //   }
  // };


  const handleInvoiceJobType = (jobType) => {
    if (["professional", "pick up/delivery"].includes(jobType.toLowerCase())) {
      return "SERVICE";
    } else if (jobType.toLowerCase() === "equipment rental") {
      return "";
    } else if (jobType.toLowerCase() === "reimbursement total") {
      return "";
    } else if (jobType.includes("Lump Sum")) {
      return "";
    } else if (jobType.toLowerCase() === "equipment fees") {
      return "LUMP SUM"; // Sirf "LUMP SUM" return karo
    } else {
      return "MATERIAL";
    }
  };



  function formatAddress(address) {
    return address.replace(/(\d+)/, "\n$1");
  }

  const creditHighlightClass = (value) =>
    (Number(value) || 0) > 0 ? "text-red-600" : "";

  // console.log("Credits Formdata", materialData, laborData);

  return (
    <Layout>
      <ToastContainer />
      <div
        className={`${tableSize === 250 ? "md:ml-[250px]" : "md:ml-[90px]"}`}
      >
        <div className="lg:p-10 p-3">
          <div className="card">
            <div className="card-header bg-[#00613e] text-white">
              <h3 className="card-title">
                <button
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <i className="fa fa-arrow-left mr-2"></i>
                </button>{" "}
                View Customer Copy
              </h3>
            </div>

            {/* Modal For Save As */}
            <div
              className="modal fade"
              id="exampleModalCenter_saveAs"
              tabIndex={-1}
              role="dialog"
              aria-labelledby="exampleModalCenterTitle"
              aria-hidden="true"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="exampleModalLongTitle">
                      Save Document As
                    </h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label htmlFor="documentName">Document Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="documentName"
                        placeholder="Enter Document Name"
                        value={documentName}
                        onChange={(e) => {
                          updateDocumentName(e.target.value);
                        }}
                        name="documentName"
                        maxLength={200}
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      data-dismiss="modal"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn bg-[#00613e] text-white"
                      data-dismiss="modal"
                      // style={{pageBreakInside:"avoid"}}
                      onClick={() => {
                        saveDocumentAs();
                      }}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Download Copy */}
            <div
              className=""
              style={{
                display: "none",
              }}
            >
              <div className="p-0 mb-20" id="content-to-pdf">
                {/* Project Data */}
                <div className="flex flex-row gap-2 justify-between mt-1">
                  <div className="flex flex-col w-1/3 md:w-[280px]">
                    <div className="p-0 capitalize">
                      <p className="text-xs font-medium">
                        <span className="border-b border-black pb-[7px]">
                          PROJECT LOCATION
                        </span>
                      </p>
                    </div>
                    {/* {formData?.customerType === "Commercial" && (
                      <div className="p-0 mt-1">
                        <p className="text-xs break-words capitalize">
                          {formData?.customerName?.toUpperCase()}
                        </p>
                      </div>
                    )} */}
                    <div className="p-0 mt-0.5">
                      <p className="text-xs break-words capitalize">
                        {formData?.customerName?.toUpperCase()}
                      </p>
                    </div>
                    <div className="p-0 capitalize">
                      <p className="text-xs break-words">
                        {formData?.jobAddress?.toUpperCase()}
                      </p>
                    </div>
                    {formData && formData.billAddress && (
                      <div className="p-0 capitalize mt-0.5">
                        <h6 className="text-xs font-medium ">
                          <span className="border-b border-black pb-[7px]">
                            BILLING ADDRESS
                          </span>
                        </h6>
                        <p className="text-xs break-words">
                          {formatAddress(formData?.billAddress?.toUpperCase())
                            .split("\n")
                            .map((line, index) => (
                              <React.Fragment key={index}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                        </p>
                      </div>
                    )}
                    {formData && formData?.billingName && (
                      <div className="p-0">
                        <p className="text-xs break-words">
                          {formData?.billingName}
                        </p>
                      </div>
                    )}
                    {formData && formData?.customerEmail && (
                      <div className="p-0">
                        <p className="text-xs break-words">
                          {formData?.customerEmail}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col w-1/3 md:w-[280px]">
                    {/* F&G Logo */}
                    <div className="flex justify-center w-full">
                      <img
                        src={fng_logo}
                        alt="F&G Logo"
                        className="h-[140px] w-full"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col w-1/3 md:w-[280px] text-end capitalize">
                    <div className="p-0">
                      <h6 className="font-bold text-[15px]">F&G INC</h6>
                      <pre
                        className="text-xs break-words p-0 pb-2 capitalize leading-4"
                        style={{ fontFamily: "Source Sans Pro" }}
                      >
                        {address}
                      </pre>
                    </div>
                    <div className="p-0">
                      <p className="text-xs break-words">{data?.uniqueId ?? ""}</p>
                    </div>
                    <div className="p-0">
                      {/* <h6 className="font-bold text-[15px]">Project Code</h6> */}
                      {formData?.projectCompletedDate && (
                        <p className="text-xs break-words">
                          {convertMillisecondsToDate(
                            formData.projectCompletedDate
                          )}
                        </p>
                      )}
                      <p className="text-xs break-words">
                        {formData.projectCode}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Description */}
                <div className="px-0.5 mt-4">
                  <h6 className="font-semibold text-[13px] tracking-wide capitalize">
                    <span className="font-semibold text-[13px] tracking-wide capitalize pb-[7px] border-b border-black">
                      {jobType?.toUpperCase()} SERVICE
                    </span>
                    {formData.jobName && (
                      <span className="font-normal text-xs capitalize ml-2">
                        : {formData.jobName?.toUpperCase()}
                      </span>
                    )}
                  </h6>
                  <p className="text-xs break-words mt-1">
                    {parse(formData.description)}
                  </p>
                </div>

                {/* Field Copies */}
                <div
                  className="w-full mt-1 text-[15px] pt-1 pb-2"
                  style={{ pageBreakInside: "auto" }}
                >
                  {categorizedFieldCopies.length > 0 || laborData.length > 0 ? (
                    categorizedFieldCopies.map((group, index) => (
                      <div key={index} className="mb-0">
                        {/* <h4 className="font-bold text-[15px] mb-3">
{group.category}
</h4> */}
                        <table
                          className="w-full text-xs"
                          style={{
                            tableLayout: "fixed",
                            width: "100%",
                            pageBreakInside: "auto",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "46%" }} />
                            <col style={{ width: "8%" }} />
                            <col style={{ width: "7%" }} />
                            <col style={{ width: "19%" }} />
                            <col style={{ width: "20%" }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th
                                className="text-xs pb-1"
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "bottom",
                                }}
                              >
                                DESCRIPTION
                              </th>
                              <th
                                className="text-xs pb-1"
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "bottom",
                                  paddingLeft: PDF_SIZE_COLUMN_SHIFT,
                                  paddingRight: "4px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                SIZE
                              </th>
                              <th
                                className="text-xs pb-1"
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "bottom",
                                  paddingLeft: PDF_QTY_COLUMN_SHIFT,
                                  paddingRight: "4px",
                                }}
                              >
                                QUANTITY
                              </th>
                              <th
                                className="text-xs pb-1"
                                style={{
                                  textAlign: "right",
                                  verticalAlign: "bottom",
                                  paddingRight: "31px",
                                }}
                              >
                                PRICE
                              </th>
                              <th
                                className="text-xs pb-1"
                                style={{ textAlign: "right", verticalAlign: "bottom" }}
                              >
                                TOTAL
                              </th>
                            </tr>
                          </thead>
                          <tbody id="customer-copy-pdf-tbody">
                            {index === 0 ? renderCustomerCopyTableBody("pdf") : null}
                          </tbody>
                        </table>
                      </div>
                    ))
                  ) : (
                    <p className="pb-2">No field copies available.</p>
                  )}
                </div>

                <div className="mt-2">
                  <h4 className="text-[15px] font-semibold text-center mb-6">
                    INVOICE SUMMARY
                  </h4>
                </div>

                {/* Compiled data by Job Type */}
                {/* <div className="mt-1 text-xs">
                  {materialData.map((material) => {
                    return (
                      <div className="flex justify-between mt-1">
                        <span>
                          <b className="w-[200px] inline-block">
                            {material.jobType?.toUpperCase()}{" "}
                            {material.source === "Labor"
                              ? "LABOR"
                              : handleInvoiceJobType(material.jobType)}
                          </b>
                          <b>
                            {formData?.customerType === "Normal"
                              ? ["Labor", "Other"].includes(material.source)
                                ? material.isTaxable
                                  ? "RT"
                                  : "RNT"
                                : isLumpSumFieldCopySource(material.source)
                                ? `${
                                    material.isTaxable ? "RT" : "RNT"
                                  } ${
                                    String(material.type || material.jobType || "").toLowerCase().includes("lodging")
                                      ? "(SALES TAX PAID ON LODGING)"
                                      : "(SALES TAX PAID ON MATERIAL)"
                                  }`
                                : "RT"
                              : formData.customerType === "Commercial"
                              ? "CT"
                              : formData?.customerType?.toUpperCase()}
                          </b>
                        </span>
                        <span>
                          <b>$</b>{" "}
                          <span className="inline-block w-[80px] text-end">
                            {material.totalPrice?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                  {laborData
                    .filter((labor) => labor.totalPrice !== 0)
                    .map((labor) => {
                      return (
                        <div className="flex justify-between mt-1">
                          <span>
                            <b className="w-[200px] inline-block">
                              {labor.jobType?.toUpperCase()} LABOR
                            </b>
                            <b>
                              {formData?.customerType === "Normal"
                                ? labor.isLaborTaxable
                                  ? "RT"
                                  : "RNT"
                                : formData.customerType === "Commercial"
                                ? "CT"
                                : formData?.customerType?.toUpperCase()}
                            </b>
                          </span>
                          <span>
                            <b>$</b>{" "}
                            <span className="inline-block w-[80px] text-end">
                              {labor.totalPrice?.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                </div> */}

                <div className="mt-1 text-xs" id="customer-copy-pdf-summary">
                  {renderCustomerCopyJobTypeSummary(true)}
                  {renderCustomerCopyFuelSurcharge(true)}
                </div>

                {/* Invoice Summary */}
                <div className="w-full mt-5 text-[15px]">
                  <div className="text-xs">
                    {true && (
                      <>
                        <div
                          className="flex justify-between my-2"
                          style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                        >
                          <span className={creditHighlightClass(formData.taxCredits)}>
                            TAX CREDITS{" "}
                            {formData.taxDescription && (
                              <>
                                (
                                {formData.taxDescription.length > 50
                                  ? formData.taxDescription.slice(0, 50) +
                                  " ..."
                                  : formData.taxDescription}
                                )
                              </>
                            )}
                          </span>
                          <span
                            className={`shrink-0 ml-auto ${creditHighlightClass(formData.taxCredits)}`}
                          >
                            {formatCurrencyDisplay(formData.taxCredits || 0, true)}
                          </span>
                        </div>
                        <div
                          className="flex justify-between my-2"
                          style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                        >
                          <span
                            className={creditHighlightClass(formData.nonTaxCredits)}
                          >
                            NON TAX CREDITS{" "}
                            {formData.nonTaxDescription && (
                              <>
                                (
                                {formData.nonTaxDescription.length > 50
                                  ? formData.nonTaxDescription.slice(0, 50) +
                                  " ..."
                                  : formData.nonTaxDescription}
                                )
                              </>
                            )}
                          </span>
                          <span
                            className={`shrink-0 ml-auto ${creditHighlightClass(formData.nonTaxCredits)}`}
                          >
                            {formatCurrencyDisplay(formData.nonTaxCredits || 0, true)}
                          </span>
                        </div>
                      </>
                    )}
                    <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                      <hr />
                      <div className="flex justify-between my-1 mb-2">
                        <span>SUBTOTAL</span>
                        <span className="shrink-0 ml-auto">
                          {formatCurrencyDisplay(customerCopyInvoiceNetSubtotal, true)}
                        </span>
                      </div>
                    </div>
                    {/* {!formData.isProjectTaxable && (
                      <>
                        <hr />
                        <div className="flex justify-between my-2">
                          <span>CREDITS</span>
                          <span>
                            <b>$</b>{" "}
                            <span className="inline-block w-[80px] text-end">
                              {" "}
                              {formData.credits?.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </span>
                        </div>
                      </>
                    )} */}
                    <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                      <hr />
                      <div className="flex justify-between my-1 mb-2">
                        <span>TAXABLE AMOUNT</span>
                        <span className="shrink-0 ml-auto">
                          {formatCurrencyDisplay(
                            (formData.isProjectTaxable
                              ? Math.max(
                                  0,
                                  Number(taxableAmount) - Number(formData.taxCredits || 0)
                                )
                              : formData.taxCredits > taxableAmount
                                ? 0
                                : Number(taxableAmount) || 0) +
                              customerCopyFuelSurcharge,
                            true
                          )}
                        </span>
                      </div>
                    </div>
                    <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                      <hr />
                      <div className="flex justify-between my-1 mb-2">
                        <span>SALES TAX</span>
                        <span className="shrink-0 ml-auto">
                          {formatCurrencyDisplay(
                            (formData.isProjectTaxable
                              ? (taxPercent *
                                  (Number(taxableAmount) -
                                    Number(formData.taxCredits || 0))) /
                                  100
                              : formData.taxCredits > taxableAmount
                                ? 0
                                : (taxPercent * Number(taxableAmount)) / 100) +
                              (Number(taxPercent) * customerCopyFuelSurcharge) / 100,
                            true
                          )}
                        </span>
                      </div>
                    </div>
                    <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                      <hr />
                      <div className="flex justify-between my-1">
                        <span>GRAND TOTAL</span>
                        <span className="shrink-0 ml-auto">
                          <span className="border-b border-black pb-[7px] inline-block">
                            {formatCurrencyDisplay(customerCopyGrandTotal, true)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Real Copy */}
            <div className="mt-6 p-4 ">
              {/* Project Data */}
              <div className="flex flex-col md:flex-row gap-6 justify-around">
                <div className="flex flex-col w-[300px]">
                  <div className="p-2">
                    <h6 className="font-bold text-[17px]">Date</h6>
                    <p>
                      {formData?.projectStartDate
                        ? formatDate(formData.projectStartDate)
                        : ""}
                    </p>
                  </div>
                  <div className="p-2">
                    <h6 className="font-bold text-[17px]">Customer Name</h6>
                    <p>{formData?.customerName}</p>
                  </div>
                  {formData && formData?.jobAddress && (
                    <div className="p-2">
                      <h6 className="font-bold text-[17px]">Job Address</h6>
                      <p>{formData?.jobAddress}</p>
                    </div>
                  )}
                  {formData && formData?.customerEmail && (
                    <div className="p-2">
                      <h6 className="font-bold text-[17px]">Email</h6>
                      <p>{formData?.customerEmail}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col w-[300px]">
                  <div className="p-2">
                    <h6 className="font-bold text-[17px]">Project Code</h6>
                    <p>{formData.projectCode}</p>
                  </div>
                  <div className="p-2">
                    <h6 className="font-bold text-[17px]">Job Type</h6>
                    <p>{jobType}</p>
                  </div>
                  {formData.description && (
                    <div className="p-2">
                      <h6 className="font-bold text-[17px]">
                        Description of work
                      </h6>
                      <p>{parse(formData.description)}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col w-[300px]">
                  <div className="p-2">
                    <h6 className="font-bold text-[17px]">F&G INC</h6>
                    <pre
                      className="text-base break-words p-0 pb-3"
                      style={{ fontFamily: "Source Sans Pro" }}
                    >
                      {address}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Field Copies */}
              <div className="w-full mt-10 text-[15px] overflow-x-auto">
                {!loading ? (
                  categorizedFieldCopies.length > 0 || laborData.length > 0 ? (
                    categorizedFieldCopies.map((group, index) => (
                      <div key={index} className="mb-8">
                        <h4 className="font-bold text-lg mb-3">
                          {group.category}
                        </h4>
                        <table className="w-full table table-striped text-start">
                          <thead className="bg-[#00613e] text-white">
                            <tr>
                              {/* <th>Source</th> */}
                              <th>Description</th>
                              <th>Size</th>
                              <th>Quantity</th>
                              <th>Cost</th>
                              <th>Markup</th>
                              <th>Price</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody id="customer-copy-view-tbody">
                            {index === 0 ? renderCustomerCopyTableBody("view") : null}
                          </tbody>
                        </table>
                      </div>
                    ))
                  ) : (
                    <p className="pb-2">No field copies available.</p>
                  )
                ) : (
                  <p className="pb-2">Loading ...</p>
                )}
              </div>

              <div className="">
                <h4 className="text-lg font-semibold text-center mb-6">
                  Invoice Summary
                </h4>
              </div>

              {/* Compiled data by Job Type */}
              <div className="mt-6 mb-4" id="customer-copy-view-summary">
                {renderCustomerCopyJobTypeSummary(false)}
                {renderCustomerCopyFuelSurcharge(false)}
                {/* {laborData
                  .filter((labor) => labor.totalPrice !== 0)
                  .map((labor) => {
                    return (
                      <div className="flex justify-between mt-1">
                        <span>
                          <b>{labor.jobType} Labor</b>
                        </span>
                        <span>
                          <b>$</b> {labor.totalPrice?.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                        </span>
                      </div>
                    );
                  })} */}
              </div>

              {/* Invoice Summary */}

              <div className="">
                <div className="">
                  {/* <div className="flex justify-between">
                    <span>Total Labor Cost</span>
                    <span>
                      <b>$</b> {laborTotal?.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between my-2">
                    <span>Total Material Cost</span>
                    <span>
                      <b>$</b> {materialsTotal?.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div> */}
                  {true && (
                    <>
                      {/* <div className="flex justify-between my-2">
                        <span>Credits</span>
                        <span>
                          <b>$</b>{" "}
                          <input
                            type="text"
                            className="w-16 outline-none border-b px-1 text-end"
                            value={formData.credits}
                            onInput={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, "");

                              if (Number(val) > 10000000000) return; // Prevent setting if greater
                              setFormData({ ...formData, credits: val });
                            }}
                          />
                        </span>
                      </div> */}
                      <div className="flex justify-between my-2">
                        <span className={creditHighlightClass(formData.taxCredits)}>
                          Tax Credits{" "}
                          {formData.taxDescription && (
                            <>
                              (
                              {formData.taxDescription.length > 50
                                ? formData.taxDescription.slice(0, 50) + " ..."
                                : formData.taxDescription}
                              )
                            </>
                          )}
                        </span>
                        <span className={creditHighlightClass(formData.taxCredits)}>
                          <b>$</b>{" "}
                          <input
                            type="text"
                            className={`w-16 outline-none border-b px-1 text-end ${creditHighlightClass(formData.taxCredits)}`}
                            value={formData.taxCredits}
                            onInput={(e) => {
                              let val = e.target.value.replace(/[^0-9]/g, "");

                              // If input is empty, default to "0"
                              val = val === "" ? "0" : val;

                              if (Number(val) > 10000000000) return; // Prevent setting if greater
                              setFormData({
                                ...formData,
                                taxCredits: Number.parseFloat(val),
                              });
                            }}
                          />
                        </span>
                      </div>
                      <div className="flex justify-between my-2">
                        <span
                          className={creditHighlightClass(formData.nonTaxCredits)}
                        >
                          Non Tax Credits{" "}
                          {formData.nonTaxDescription && (
                            <>
                              (
                              {formData.nonTaxDescription.length > 50
                                ? formData.nonTaxDescription.slice(0, 50) +
                                " ..."
                                : formData.nonTaxDescription}
                              )
                            </>
                          )}
                        </span>
                        <span
                          className={creditHighlightClass(formData.nonTaxCredits)}
                        >
                          <b>$</b>{" "}
                          <input
                            type="text"
                            className={`w-16 outline-none border-b px-1 text-end ${creditHighlightClass(formData.nonTaxCredits)}`}
                            value={formData.nonTaxCredits}
                            onInput={(e) => {
                              let val = e.target.value.replace(/[^0-9]/g, "");
                              // If input is empty, default to "0"
                              val = val === "" ? "0" : val;
                              if (Number(val) > 10000000000) return; // Prevent setting if greater
                              setFormData({
                                ...formData,
                                nonTaxCredits: Number.parseFloat(val),
                              });
                            }}
                          />
                        </span>
                      </div>
                    </>
                  )}
                  <hr />

                  <div className="flex justify-between my-2">
                    <span>SubTotal</span>
                    <span>
                      <b>$</b>{" "}
                      {customerCopyInvoiceNetSubtotal.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <hr />
                </div>
              </div>

              {formData &&
                formData.taxCredits + formData.nonTaxCredits <=
                customerCopyInvoiceGrossSubtotal ? (
                !loading ? (
                  formData && formData.taxCredits <= taxableAmount ? (
                    <div className="">
                      {/* <div className="flex justify-between my-2">
                                <span>Credits</span>
                                <span>
                                  <b>$</b>{" "}
                                  {formData.credits?.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div> */}
                      {/* {!formData.isProjectTaxable && (
                                <>
                                  <div className="flex justify-between my-2">
                                    <span>Credits</span>
                                    <span>
                                      <b>$</b>{" "}
                                      {formData.credits?.toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                </>
                              )} */}
                      <hr />
                      <div className="flex justify-between my-2">
                        <span>Taxable Amount</span>
                        <span>
                          <b>$</b>{" "}
                          {formData.isProjectTaxable
                            ? (
                              taxableAmount - formData.taxCredits + customerCopyFuelSurcharge
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : (
                              Number(taxableAmount) + customerCopyFuelSurcharge
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </span>
                      </div>
                      <hr />
                      <div className="flex justify-between my-2">
                        <span>Sales Tax</span>
                        <span>
                          <b>$</b>{" "}
                          {formData.isProjectTaxable
                            ? (
                              (taxPercent *
                                (taxableAmount - formData.taxCredits + customerCopyFuelSurcharge)) /
                              100
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : (
                              (taxPercent * (Number(taxableAmount) + customerCopyFuelSurcharge)) /
                              100
                            )?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                        </span>
                      </div>
                      <hr />
                      <div className="flex justify-between my-2">
                        <span>Grand Total</span>
                        <span>
                          <b>$</b>{" "}
                          {customerCopyGrandTotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {formData.isProjectTaxable ? (
                        <div className="h-[150px] w-full flex justify-center items-center">
                          Please ensure the credits are less than the taxable
                          amount.
                        </div>
                      ) : (
                        <div className="">
                          <hr />
                          <div className="flex justify-between my-2">
                            <span>Taxable Amount</span>
                            <span>
                              <b>$</b>{" "}
                              {0?.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                          <hr />
                          <div className="flex justify-between my-2">
                            <span>Sales Tax</span>
                            <span>
                              <b>$</b>{" "}
                              {(0 / 100)?.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                          <hr />
                          <div className="flex justify-between my-2">
                            <span>Grand Total</span>
                            <span>
                              <b>$</b>{" "}
                              {customerCopyGrandTotal.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <div className="h-[150px] w-full flex justify-center items-center">
                    Loading ...
                  </div>
                )
              ) : (
                <div className="h-[150px] w-full flex justify-center items-center">
                  Please ensure the credits are less than the sub total amount
                </div>
              )}

              <hr />
            </div>

            <div className="w-full mb-10 text-[15px] flex justify-end flex-col md:flex-row gap-4 p-6">
              <button
                className={`bg-[#00613e] text-white py-1 px-6 md:mr-3 mr-0 ${(formData &&
                  formData.taxCredits > taxableAmount &&
                  formData.isProjectTaxable) ||
                  customerCopyInvoiceGrossSubtotal <
                  formData.taxCredits + formData.nonTaxCredits
                  ? "cursor-not-allowed"
                  : "cursor-pointer"
                  }`}
                type="button"
                data-toggle="modal"
                data-target="#exampleModalCenter_saveAs"
                data-dismiss="modal"
                disabled={
                  (formData &&
                    formData.taxCredits > taxableAmount &&
                    formData.isProjectTaxable) ||
                  customerCopyInvoiceGrossSubtotal <
                  formData.taxCredits + formData.nonTaxCredits
                }
              // onClick={downloadPdf}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
